import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import LogoWhite from '../assets/LOGO_WHITE.png';
import Footer from '../components/Footer';

const BG_01 = "https://firebasestorage.googleapis.com/v0/b/outlne-6a95c.firebasestorage.app/o/ui_images_backgrounds%2FBG_01.jpg?alt=media&token=9e891581-27bf-4bdd-83cf-a39b44af9324";
const BG_02 = "https://firebasestorage.googleapis.com/v0/b/outlne-6a95c.firebasestorage.app/o/ui_images_backgrounds%2FBG_02.jpg?alt=media&token=b4394dc2-756e-41e8-9cfd-53c25137835d";
const BG_03 = "https://firebasestorage.googleapis.com/v0/b/outlne-6a95c.firebasestorage.app/o/ui_images_backgrounds%2FBG_03.jpg?alt=media&token=989909db-898b-4f93-babf-0262e968ddff";
const BG_04 = "https://firebasestorage.googleapis.com/v0/b/outlne-6a95c.firebasestorage.app/o/ui_images_backgrounds%2FBG_04.jpg?alt=media&token=b895a70e-d345-48b9-aa06-a359057f059a";
const BG_05 = "https://firebasestorage.googleapis.com/v0/b/outlne-6a95c.firebasestorage.app/o/ui_images_backgrounds%2FBG_05.jpg?alt=media&token=d91d94d3-064a-4efa-8bcf-f37cd4a04714";

const PRIVACY_HTML = `
<h3>1. Introduction</h3>
<p>Outlne, Inc. ("Outlne," "we," "us") is committed to protecting your privacy. This Privacy Policy explains how we collect, use, disclose, and safeguard your personal information when you use our Service.</p>

<h3>2. Information We Collect</h3>
<p><strong>2.1. Information You Provide:</strong></p>
<ul>
  <li>Account data: first name, last name, email address, birthday, password (encrypted), home type (apartment/condo/house).</li>
  <li>User Content: photographs, motion path videos, 3D models, textures, text prompts, project descriptions.</li>
  <li>Payment information: payment card details are processed by our PCI‑compliant payment processor; we do not store full card numbers.</li>
</ul>
<p><strong>2.2. Automatically Collected Information:</strong></p>
<ul>
  <li>Device and usage data: IP address, browser type, operating system, referral URLs, pages viewed, feature usage, and error logs.</li>
  <li>Cookies and similar technologies (see Cookie Policy).</li>
</ul>
<p><strong>2.3. Generative Processing Data:</strong></p>
<ul>
  <li>When you use our visualization tools, your full photo and a mask image (generated from your selection) are transmitted to third‑party processing providers. For video rendering, your motion path video (a recording of a real physical space) is also transmitted. These providers process data in memory; we have selected privacy‑protective vendors but have not obtained contractual guarantees against training use. See our Data Processing Agreement.</li>
  <li>Voice input from Speak and Create is converted to text entirely on‑device; audio never leaves your device and is not uploaded to Outlne or any third party.</li>
</ul>

<h3>3. How We Use Your Information</h3>
<p>We use your information to:</p>
<ul>
  <li>Provide, maintain, and improve the Service;</li>
  <li>Process transactions and manage your generation budget;</li>
  <li>Send administrative messages (security alerts, account notices);</li>
  <li>Personalize your experience and provide customer support;</li>
  <li>Analyze usage patterns to develop new features;</li>
  <li>Detect, prevent, and address fraud, abuse, and security incidents;</li>
  <li>Comply with legal obligations.</li>
</ul>

<h3>4. Legal Bases (for UK/EEA Users)</h3>
<p>For users in the UK or European Economic Area, we rely on the following legal bases:</p>
<ul>
  <li>Performance of a contract (providing the Service);</li>
  <li>Legitimate interests (improving the Service, securing the platform);</li>
  <li>Consent (for non‑essential cookies and marketing);</li>
  <li>Compliance with legal obligations.</li>
</ul>

<h3>5. Data Sharing and Disclosure</h3>
<p><strong>5.1. Service Providers:</strong> We share data with trusted third‑party vendors who perform services on our behalf, including:</p>
<ul>
  <li>Cloud hosting and storage (Firebase/Google Cloud)</li>
  <li>Payment processing (Stripe)</li>
  <li>Image and video processing (third‑party GPU cloud providers, e.g., Replicate and others; full list available under NDA)</li>
  <li>Image search (Unsplash)</li>
  <li>Email delivery</li>
  <li>Analytics (if enabled)</li>
</ul>
<p>These providers are contractually bound to process data only as instructed and in compliance with applicable data protection laws.</p>
<p><strong>5.2. Legal Compliance:</strong> We may disclose information if required by law, subpoena, or government request.</p>
<p><strong>5.3. Business Transfers:</strong> In the event of a merger, acquisition, or asset sale, your data may be transferred, provided the recipient agrees to honor this Privacy Policy.</p>
<p><strong>5.4. Aggregated/De‑Identified Data:</strong> We may share non‑identifiable, aggregated data for research or marketing.</p>

<h3>6. International Data Transfers</h3>
<p>Your data may be transferred to and processed in the United States and other countries. For transfers from the UK/EEA, we ensure appropriate safeguards, such as Standard Contractual Clauses (SCCs) or the UK International Data Transfer Agreement. You may request a copy of these safeguards by contacting us.</p>

<h3>7. Data Retention</h3>
<p>We retain personal data only as long as necessary to fulfill the purposes outlined in this policy. Specific retention periods are detailed in our Data Retention and Deletion Policy.</p>

<h3>8. Your Rights</h3>
<p>Depending on your jurisdiction, you may have the right to:</p>
<ul>
  <li>Access, correct, or delete your personal data;</li>
  <li>Object to or restrict processing;</li>
  <li>Data portability;</li>
  <li>Withdraw consent (where processing is based on consent);</li>
  <li>Opt out of the sale/sharing of personal information (California residents under CCPA);</li>
  <li>File a complaint with a supervisory authority.</li>
</ul>
<p>To exercise your rights, email privacy@outlne.com. We will respond within legally required timeframes.</p>

<h3>9. Cookies and Tracking</h3>
<p>We use essential cookies for authentication and security. With your consent (where required, e.g., UK/EEA), we may also use analytics cookies. You can manage cookie preferences through your browser settings. See our Cookie Policy.</p>

<h3>10. Children's Privacy</h3>
<p>The Service is not directed to individuals under 18. We do not knowingly collect data from children. If we become aware of such collection, we will delete it promptly.</p>

<h3>11. Security</h3>
<p>We implement appropriate technical and organizational measures, including encryption at rest and in transit, access controls, and regular audits. However, no method is 100% secure.</p>

<h3>12. Changes to This Policy</h3>
<p>We will notify you of material updates via email or in‑app notice. Continued use after changes become effective constitutes acceptance.</p>

<h3>13. Contact Us</h3>
<p>Email: privacy@outlne.com</p>
`;

export default function Privacy() {
  useEffect(() => {
    document.title = "Privacy Policy | Outlne";
  }, []);

  const navigate = useNavigate();
  const [isMobile, setIsMobile] = useState(false);
  const [isTablet, setIsTablet] = useState(false);
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  const [activeGrid, setActiveGrid] = useState(0);
  const [scrollPosition, setScrollPosition] = useState({ x: 0, y: 0 });
  const [bgIndex, setBgIndex] = useState(0);
  const [bgPosition, setBgPosition] = useState({ x: 0, y: 0 });
  const [bgScale, setBgScale] = useState(1);
  const [bgOpacity, setBgOpacity] = useState(0);
  const [gridOpacity, setGridOpacity] = useState(1);
  const [pageLoaded, setPageLoaded] = useState(false);

  const lastGridChangeTime = useRef(0);
  const bgIntervalRef = useRef(null);
  const bgAnimationRef = useRef(null);

  const backgrounds = [BG_01, BG_02, BG_03, BG_04, BG_05];

  // Check screen size
  useEffect(() => {
    const checkScreenSize = () => {
      const width = window.innerWidth;
      const mobile = width < 768;
      const tablet = width >= 768 && width < 1224;
      
      setIsMobile(mobile);
      setIsTablet(tablet);
      setWindowWidth(width);
    };
    
    checkScreenSize();
    window.addEventListener('resize', checkScreenSize);
    return () => window.removeEventListener('resize', checkScreenSize);
  }, []);

  // Page load effect - fade out grid and fade in background
  useEffect(() => {
    const timer = setTimeout(() => {
      setPageLoaded(true);
      setGridOpacity(0);
      setBgOpacity(0);
    }, 100);

    return () => clearTimeout(timer);
  }, []);

  // Optimized background slideshow
  useEffect(() => {
    const preloadImages = () => {
      backgrounds.forEach(src => {
        const img = new Image();
        img.src = src;
      });
    };
    preloadImages();

    const changeBackground = () => {
      setBgIndex(prev => (prev + 1) % backgrounds.length);
      
      const direction = Math.random() > 0.5 ? 1 : -1;
      const axis = Math.random() > 0.5 ? 'x' : 'y';
      
      setBgPosition({ x: 0, y: 0 });
      setBgScale(1.05);
      
      setTimeout(() => {
        setBgPosition({
          x: axis === 'x' ? direction * 5 : 0,
          y: axis === 'y' ? direction * 5 : 0
        });
      }, 10);
    };

    if (pageLoaded) {
      changeBackground();
      bgIntervalRef.current = setInterval(changeBackground, 8000);
    }

    return () => {
      clearInterval(bgIntervalRef.current);
      if (bgAnimationRef.current) {
        cancelAnimationFrame(bgAnimationRef.current);
      }
    };
  }, [pageLoaded]);

  // Grid animation - only run after page has loaded
  useEffect(() => {
    if (!pageLoaded) return;

    let animationFrame;
    let lastTime = 0;
    const scrollSpeed = 10;

    const animateScroll = (time) => {
      if (!lastTime) lastTime = time;
      const delta = (time - lastTime) / 1000;
      lastTime = time;
      
      const timeSinceChange = time - lastGridChangeTime.current;
      
      if (timeSinceChange > 100) {
        setScrollPosition(prev => ({
          x: (prev.x + scrollSpeed * delta) % 80,
          y: (prev.y + scrollSpeed * delta) % 80
        }));
      }
      
      animationFrame = requestAnimationFrame(animateScroll);
    };

    animationFrame = requestAnimationFrame(animateScroll);

    const interval = setInterval(() => {
      lastGridChangeTime.current = performance.now();
      setScrollPosition({ x: 0, y: 0 });
      setActiveGrid((prev) => (prev + 1) % 3);
    }, 4000);

    return () => {
      cancelAnimationFrame(animationFrame);
      clearInterval(interval);
    };
  }, [pageLoaded]);

  // Grid styles
  const gridStyles = [
    {
      transform: `translate(-${scrollPosition.x}px, -${scrollPosition.y}px)`,
      backgroundSize: '40px 40px',
      display: activeGrid === 0 ? 'block' : 'none',
      opacity: gridOpacity,
      transition: 'opacity 1s ease'
    },
    {
      transform: `translate(-${scrollPosition.x}px, -${scrollPosition.y}px) skewX(-15deg)`,
      backgroundSize: '50px 40px',
      backgroundPositionX: '20px',
      display: activeGrid === 1 ? 'block' : 'none',
      opacity: gridOpacity,
      transition: 'opacity 1s ease'
    },
    {
      transform: `translate(-${scrollPosition.x}px, -${scrollPosition.y}px) perspective(500px) rotateX(15deg)`,
      backgroundSize: '40px 30px',
      backgroundPositionY: '10px',
      display: activeGrid === 2 ? 'block' : 'none',
      opacity: gridOpacity,
      transition: 'opacity 1s ease'
    }
  ];

  return (
    <>
      {/* Fixed Background Layer */}
      <div style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(29, 29, 29, 0.8)',
        zIndex: -3,
      }} />

      {/* Background Slideshow */}
      <div style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        zIndex: -2,
        overflow: 'hidden',
        willChange: 'transform',
        opacity: bgOpacity,
        transition: 'opacity 1s ease'
      }}>
        <div style={{
          position: 'absolute',
          top: '-10%',
          left: '-10%',
          right: '-10%',
          bottom: '-10%',
          backgroundImage: `url(${backgrounds[bgIndex]})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          transform: `scale(${bgScale}) translate(${bgPosition.x}%, ${bgPosition.y}%)`,
          mixBlendMode: 'soft-light',
          transition: 'background-image 1s ease-in-out, transform 8s linear',
          willChange: 'transform, opacity',
          backfaceVisibility: 'hidden',
          transformStyle: 'preserve-3d',
        }} />
      </div>

      {/* Grid Container - Fades out after load */}
      <div style={{
        position: 'fixed',
        top: 0,
        left: 0,
        width: isMobile ? '100vw' : '50vw',
        bottom: 0,
        zIndex: -1,
        overflow: 'hidden',
        opacity: gridOpacity,
        transition: 'opacity 1s ease'
      }}>
        <div style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          width: isMobile ? '100vw' : '200vw',
          maskImage: isMobile ? 'none' : 'linear-gradient(to right, transparent 0%, white 5%, white 95%, transparent 100%)',
          WebkitMaskImage: isMobile ? 'none' : 'linear-gradient(to right, transparent 0%, white 5%, white 95%, transparent 100%)',
        }}>
          {gridStyles.map((style, index) => (
            <div key={index} style={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundImage: `
                linear-gradient(to right, white 1px, transparent 1px),
                linear-gradient(to bottom, white 1px, transparent 1px)
              `,
              ...style
            }} />
          ))}
        </div>
      </div>

      {/* Desktop Header */}
      {!isMobile && (
        <>
          <div 
            style={{
              position: 'fixed',
              top: '30px',
              left: '30px',
              zIndex: 20,
              cursor: 'pointer',
              fontSize: '2rem',
              color: 'white',
            }}
            onClick={() => navigate('/')}
          >
            ×
          </div>

          <div style={{
            position: 'fixed',
            top: '40px',
            right: '40px',
            zIndex: 10,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'flex-start',
            textAlign: 'left',
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              <img 
                src={LogoWhite} 
                alt="Outlne Logo"
                style={{
                  width: '40px',
                  height: 'auto',
                }}
              />
              <h1 style={{
                fontSize: '1.8rem',
                fontFamily: 'avenir-lt-pro, sans-serif',
                letterSpacing: '-1.2px',
                margin: 0,
                color: 'white',
                fontWeight: 500,
              }}>
                Outlne
              </h1>
            </div>
            <p style={{
              fontSize: '1rem',
              margin: '5px 0 0 0',
              color: 'white',
              fontFamily: 'avenir-lt-pro, sans-serif',
              fontWeight: '500'
            }}>
              Interior Design <span style={{
                backgroundColor: '#ffffff',
                color: 'black',
                padding: '2px 8px',
                borderRadius: '4px',
                display: 'inline-block',
                minWidth: '60px',
                textAlign: 'center'
              }}>
                Editor
              </span>
            </p>
          </div>
        </>
      )}

      {/* Mobile Header with Blur Background */}
      {isMobile && (
        <>
          <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            height: '110px',
            backgroundColor: 'rgba(31, 31, 31, 0.8)',
            backdropFilter: 'blur(10px)',
            zIndex: 15,
          }} />
          
          <div style={{
            position: 'fixed',
            top: '20px',
            left: '20px',
            zIndex: 20,
            textAlign: 'left',
          }}>
            <h1 style={{
              fontSize: '1.5rem',
              fontFamily: 'avenir-lt-pro, sans-serif',
              letterSpacing: '-1.2px',
              margin: 0,
              color: 'white',
              fontWeight: 500,
            }}>
              Outlne
            </h1>
            <p style={{
              fontSize: '0.9rem',
              margin: '5px 0 0 0',
              color: 'white',
              fontFamily: 'avenir-lt-pro, sans-serif',
              fontWeight: '500'
            }}>
              Interior Design <span style={{
                backgroundColor: '#ffffff',
                color: 'black',
                padding: '2px 6px',
                borderRadius: '4px',
                display: 'inline-block',
                minWidth: '50px',
                textAlign: 'center'
              }}>
                Editor
              </span>
            </p>
          </div>

          <div style={{
            position: 'fixed',
            top: '20px',
            right: '20px',
            zIndex: 20,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'flex-end',
            gap: '10px',
          }}>
            <img 
              src={LogoWhite} 
              alt="Outlne Logo"
              style={{
                width: '40px',
                height: '40px',
              }}
            />
            <div 
              style={{
                cursor: 'pointer',
                marginRight: '10px',
                fontSize: '1.8rem',
                color: 'white',
                marginTop: '-5px',
              }}
              onClick={() => navigate('/')}
            >
              ×
            </div>
          </div>
        </>
      )}

      {/* Main Content Container - Scrollable with Footer */}
      <div style={{
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        zIndex: 5,
        overflowY: 'auto',
        overflowX: 'hidden',
        opacity: pageLoaded ? 1 : 0,
        transition: 'opacity 1s ease',
        overscrollBehavior: 'none'
      }}>
        
        {/* Content Wrapper */}
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          width: '100%',
          minHeight: '100vh',
          paddingTop: isMobile ? '130px' : '150px',
          paddingBottom: '0',
          position: 'relative',
          zIndex: 5,
        }}>
          
          {/* Scrollable Modal Container */}
          <div style={{
            width: isMobile ? '85vw' : isTablet ? '55vw' : '600px',
            height: isMobile ? 'auto' : isTablet ? 'auto' : 'auto',
            minHeight: isMobile ? 'auto' : '500px',
            border: '2px solid white',
            borderRadius: '40px',
            overflow: 'hidden',
            padding: isMobile ? '15px' : '20px',
            boxSizing: 'border-box',
            display: 'flex',
            flexDirection: 'column',
            backgroundColor: 'rgba(34, 34, 34, 0.95)',
            marginBottom: '3rem',
          }}>
            
            {/* Privacy Policy Header */}
            <div style={{
              textAlign: 'center',
              marginBottom: isMobile ? '1.5rem' : '2rem',
              padding: isMobile ? '0 10px' : '0 20px',
              flexShrink: 0
            }}>
              <h1 style={{
                fontFamily: 'avenir-lt-pro, sans-serif',
                fontSize: isMobile ? '1.4rem' : isTablet ? '1.6rem' : '1.8rem',
                fontWeight: '500',
                color: 'white',
                marginBottom: '0.5rem',
                lineHeight: '1.2',
                letterSpacing: '-0.5px'
              }}>
                Privacy Policy
              </h1>
              <p style={{
                color: '#ccc',
                fontSize: isMobile ? '0.9rem' : isTablet ? '1rem' : '1.1rem',
                lineHeight: '1.4',
                fontFamily: 'avenir-lt-pro, sans-serif',
                margin: 0
              }}>
                Last Updated: August 11, 2026
              </p>
              <div style={{
                marginTop: '0.25rem',
                fontSize: isMobile ? '0.75rem' : '0.85rem',
                color: '#40a3ff',
                fontFamily: 'avenir-lt-pro, sans-serif',
              }}>
                <a href="/policycenter" style={{ color: '#40a3ff', textDecoration: 'none' }}>
                  View Policy Center
                </a>
              </div>
            </div>

            {/* Scrollable Privacy Policy Content */}
            <div style={{
              flex: 1,
              overflowY: 'auto',
              padding: isMobile ? '0 5px' : '0 10px',
              scrollbarWidth: 'thin',
              scrollbarColor: 'rgba(255,255,255,0.3) transparent',
              backgroundColor: 'rgba(27, 27, 27, 0.9)',
              borderRadius: '12px',
              maxHeight: isMobile ? '400px' : '500px',
            }}>
              <style>
                {`
                  ::-webkit-scrollbar {
                    width: 6px;
                  }
                  ::-webkit-scrollbar-track {
                    background: transparent;
                  }
                  ::-webkit-scrollbar-thumb {
                    background: rgba(255,255,255,0.3);
                    border-radius: 3px;
                  }
                  ::-webkit-scrollbar-thumb:hover {
                    background: rgba(255,255,255,0.5);
                  }
                `}
              </style>
              
              <div 
                className="policy-content"
                style={{
                  padding: isMobile ? '15px 5px 20px 5px' : '20px 10px 20px 10px',
                  color: '#ccc',
                  fontSize: isMobile ? '0.85rem' : '0.95rem',
                  lineHeight: '1.6',
                }}
                dangerouslySetInnerHTML={{ __html: PRIVACY_HTML }}
              />
            </div>

            {/* Footer inside modal */}
            <div style={{
              textAlign: 'center',
              paddingTop: isMobile ? '1rem' : '1.5rem',
              marginTop: 'auto',
              flexShrink: 0,
              borderTop: '1px solid rgba(255, 255, 255, 0.2)'
            }}>
              <button
                onClick={() => navigate('/')}
                style={{
                  padding: isMobile ? '10px 20px' : '12px 24px',
                  backgroundColor: 'white',
                  color: '#000',
                  border: 'none',
                  borderRadius: '25px',
                  fontFamily: 'avenir-lt-pro, sans-serif',
                  fontWeight: '500',
                  fontSize: isMobile ? '0.9rem' : '0.95rem',
                  cursor: 'pointer',
                  transition: 'background-color 0.3s ease',
                }}
                onMouseOver={(e) => e.currentTarget.style.backgroundColor = '#f0f0f0'}
                onMouseOut={(e) => e.currentTarget.style.backgroundColor = 'white'}
              >
                Close
              </button>
            </div>
          </div>

          {/* Footer */}
          <div style={{
            width: '100%',
            position: 'relative',
            zIndex: 5,
            backgroundColor: '#1a1a1a',
          }}>
            <Footer />
          </div>
        </div>
      </div>

      {/* CSS Styles */}
      <style>
        {`
          html {
            overflow: hidden;
            height: 100%;
            width: 100%;
            position: fixed;
          }
          
          body {
            overflow: hidden;
            height: 100%;
            width: 100%;
            position: fixed;
            user-select: none;
            -webkit-user-select: none;
            -moz-user-select: none;
            margin: 0;
            padding: 0;
            overscroll-behavior: none;
            -webkit-overflow-scrolling: touch;
          }
          
          #root {
            height: 100%;
            width: 100%;
            overflow: hidden;
            position: fixed;
          }
          
          * {
            -webkit-font-smoothing: antialiased;
            -moz-osx-font-smoothing: grayscale;
            -webkit-tap-highlight-color: transparent;
            -webkit-touch-callout: none;
          }
          
          *:focus {
            outline: none;
          }
          
          div[style*="mask-image"] {
            pointer-events: none;
          }

          .policy-content h2 {
            color: #ffffff;
            font-size: 1.8rem;
            font-weight: 700;
            margin: 0 0 0.5rem 0;
            letter-spacing: -0.02em;
          }
          .policy-content h3 {
            color: #ffffff;
            font-size: 1.2rem;
            font-weight: 700;
            margin: 1.5rem 0 0.75rem 0;
            letter-spacing: -0.01em;
          }
          .policy-content p {
            color: #e0e0e0;
            font-size: 0.95rem;
            line-height: 1.8;
            margin: 0.75rem 0;
          }
          .policy-content .last-updated {
            color: #aaaaaa;
            font-size: 0.85rem;
            margin-bottom: 1.5rem;
          }
          .policy-content ul {
            margin: 0.5rem 0 0.75rem 0;
            padding-left: 1.5rem;
          }
          .policy-content li {
            color: #e0e0e0;
            font-size: 0.95rem;
            line-height: 1.8;
            margin-bottom: 0.35rem;
          }
          .policy-content strong {
            color: #ffffff;
            font-weight: 700;
          }
        `}
      </style>
    </>
  );
}